import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { FullPageLoader } from "./components/common/FullPageLoader";
import { useAuth } from "./context/AuthContext";

const LoginPage = lazy(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then((m) => ({ default: m.SignupPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const CADashboardPage = lazy(() => import("./pages/CADashboardPage").then((m) => ({ default: m.CADashboardPage })));
const BillsPage = lazy(() => import("./pages/BillsPage").then((m) => ({ default: m.BillsPage })));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage").then((m) => ({ default: m.InvoicesPage })));
const ClientsPage = lazy(() => import("./pages/ClientsPage").then((m) => ({ default: m.ClientsPage })));
const MyClientsPage = lazy(() => import("./pages/MyClientsPage").then((m) => ({ default: m.MyClientsPage })));
const MyWorkPage = lazy(() => import("./pages/MyWorkPage").then((m) => ({ default: m.MyWorkPage })));
const VendorsPage = lazy(() => import("./pages/VendorsPage").then((m) => ({ default: m.VendorsPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const CAReportsPage = lazy(() => import("./pages/CAReportsPage").then((m) => ({ default: m.CAReportsPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const ClientUploadPage = lazy(() => import("./pages/ClientUploadPage").then((m) => ({ default: m.ClientUploadPage })));
const InvitePage = lazy(() => import("./pages/InvitePage").then((m) => ({ default: m.InvitePage })));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage })));
const StockItemMappingsPage = lazy(() => import("./pages/StockItemMappingsPage").then((m) => ({ default: m.StockItemMappingsPage })));
const ApprovalsPage = lazy(() => import("./pages/ApprovalsPage").then((m) => ({ default: m.ApprovalsPage })));
const Gstr2bReconPage = lazy(() => import("./pages/Gstr2bReconPage").then((m) => ({ default: m.Gstr2bReconPage })));
const BankReconPage = lazy(() => import("./pages/BankReconPage").then((m) => ({ default: m.BankReconPage })));
const VendorStatementReconPage = lazy(() => import("./pages/VendorStatementReconPage").then((m) => ({ default: m.VendorStatementReconPage })));
const Gstr1ReconPage = lazy(() => import("./pages/Gstr1ReconPage").then((m) => ({ default: m.Gstr1ReconPage })));
const ItcTrackingPage = lazy(() => import("./pages/ItcTrackingPage").then((m) => ({ default: m.ItcTrackingPage })));
const TdsTcsPage = lazy(() => import("./pages/TdsTcsPage").then((m) => ({ default: m.TdsTcsPage })));
const BankStatementsPage = lazy(() => import("./pages/BankStatementsPage").then((m) => ({ default: m.BankStatementsPage })));
const AuditTrailPage = lazy(() => import("./pages/AuditTrailPage").then((m) => ({ default: m.AuditTrailPage })));
const RemindersPage = lazy(() => import("./pages/RemindersPage").then((m) => ({ default: m.RemindersPage })));
const ManualVouchersPage = lazy(() => import("./pages/ManualVouchersPage").then((m) => ({ default: m.ManualVouchersPage })));
const FinancialDashboardPage = lazy(() => import("./pages/FinancialDashboardPage").then((m) => ({ default: m.FinancialDashboardPage })));
const ComplianceCalendarPage = lazy(() => import("./pages/ComplianceCalendarPage").then((m) => ({ default: m.ComplianceCalendarPage })));
const EmailInboxPage = lazy(() => import("./pages/EmailInboxPage").then((m) => ({ default: m.EmailInboxPage })));
const AnomalyDetectionPage = lazy(() => import("./pages/AnomalyDetectionPage").then((m) => ({ default: m.AnomalyDetectionPage })));
const VyaparIntegrationPage = lazy(() => import("./pages/VyaparIntegrationPage").then((m) => ({ default: m.VyaparIntegrationPage })));
const AIAssistantPage = lazy(() => import("./pages/AIAssistantPage").then((m) => ({ default: m.AIAssistantPage })));
const DeveloperPage = lazy(() => import("./pages/DeveloperPage").then((m) => ({ default: m.DeveloperPage })));
const WhiteLabelPage = lazy(() => import("./pages/WhiteLabelPage").then((m) => ({ default: m.WhiteLabelPage })));
const PredictiveAnalyticsPage = lazy(() => import("./pages/PredictiveAnalyticsPage").then((m) => ({ default: m.PredictiveAnalyticsPage })));
const MultiERPPage = lazy(() => import("./pages/MultiERPPage").then((m) => ({ default: m.MultiERPPage })));
const ARAPDashboardPage = lazy(() => import("./pages/ARAPDashboardPage").then((m) => ({ default: m.ARAPDashboardPage })));
const QuotationsPage = lazy(() => import("./pages/QuotationsPage").then((m) => ({ default: m.QuotationsPage })));
const TallyQueuePage = lazy(() => import("./pages/TallyQueuePage").then((m) => ({ default: m.TallyQueuePage })));
const LandingPage = lazy(() => import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })));
const TallyPostingAutomationPage = lazy(() => import("./pages/TallyPostingAutomationPage").then((m) => ({ default: m.TallyPostingAutomationPage })));
const DocsPage = lazy(() => import("./pages/DocsPage").then((m) => ({ default: m.DocsPage })));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("./pages/TermsPage").then((m) => ({ default: m.TermsPage })));
const AppShell = lazy(() => import("./components/layout/AppShell").then((m) => ({ default: m.AppShell })));

function ProtectedPage({ children }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function DashboardRouter() {
  const { organization, user, billing } = useAuth();
  const isCa = (organization?.orgType || "").toLowerCase() === "ca_firm";
  const isCaManager = ["owner", "admin"].includes(String(user?.orgRole || "").toLowerCase());
  const canUseCaWorkspace = !billing
    || (Array.isArray(billing?.enabledModules) && billing.enabledModules.includes("multi_org"));
  if (isCa && !isCaManager && canUseCaWorkspace) return <MyWorkPage />;
  if (isCa && !isCaManager) return <BillsPage />;
  if (isCa && !canUseCaWorkspace) return <DashboardPage />;
  if (isCa) return <CADashboardPage />;
  return <DashboardPage />;
}

function ReportsRouter() {
  const { organization, user, billing } = useAuth();
  const isCa = (organization?.orgType || "").toLowerCase() === "ca_firm";
  const isCaManager = ["owner", "admin"].includes(String(user?.orgRole || "").toLowerCase());
  const canUseCaWorkspace = !billing
    || (Array.isArray(billing?.enabledModules) && billing.enabledModules.includes("multi_org"));
  if (isCa && !isCaManager && canUseCaWorkspace) return <MyWorkPage />;
  if (isCa && !isCaManager) return <ReportsPage />;
  if (isCa && !canUseCaWorkspace) return <ReportsPage />;
  if (isCa) return <CAReportsPage />;
  return <ReportsPage />;
}

function PublicHomeRoute() {
  const { isAuthenticated, isBootstrapping } = useAuth();
  if (isBootstrapping) {
    return <FullPageLoader title="Preparing" />;
  }
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

function AppRoutes() {
  const location = useLocation();

  return (
    <Suspense fallback={<FullPageLoader title="Loading" />}>
      <Routes location={location}>
        <Route path="/" element={<PublicHomeRoute />} />
        <Route path="/tally-posting-automation" element={<TallyPostingAutomationPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedPage><DashboardRouter /></ProtectedPage>} />
        <Route path="/bills" element={<ProtectedPage><BillsPage /></ProtectedPage>} />
        <Route path="/invoices" element={<ProtectedPage><InvoicesPage /></ProtectedPage>} />
        <Route path="/clients" element={<ProtectedPage><ClientsPage /></ProtectedPage>} />
        <Route path="/my-clients" element={<ProtectedPage><MyClientsPage /></ProtectedPage>} />
        <Route path="/my-work" element={<ProtectedPage><MyWorkPage /></ProtectedPage>} />
        <Route path="/vendors" element={<ProtectedPage><VendorsPage /></ProtectedPage>} />
        <Route path="/reports" element={<ProtectedPage><ReportsRouter /></ProtectedPage>} />
        <Route path="/settings" element={<ProtectedPage><SettingsPage /></ProtectedPage>} />
        <Route path="/stock-items" element={<ProtectedPage><StockItemMappingsPage /></ProtectedPage>} />
        <Route path="/approvals" element={<ProtectedPage><ApprovalsPage /></ProtectedPage>} />
        <Route path="/reconciliation/gstr2b" element={<ProtectedPage><Gstr2bReconPage /></ProtectedPage>} />
        <Route path="/reconciliation/gstr1" element={<ProtectedPage><Gstr1ReconPage /></ProtectedPage>} />
        <Route path="/reconciliation/bank" element={<ProtectedPage><BankReconPage /></ProtectedPage>} />
        <Route path="/reconciliation/vendor" element={<ProtectedPage><VendorStatementReconPage /></ProtectedPage>} />
        <Route path="/itc" element={<ProtectedPage><ItcTrackingPage /></ProtectedPage>} />
        <Route path="/tds" element={<ProtectedPage><TdsTcsPage /></ProtectedPage>} />
        <Route path="/bank-statements" element={<ProtectedPage><BankStatementsPage /></ProtectedPage>} />
        <Route path="/audit" element={<ProtectedPage><AuditTrailPage /></ProtectedPage>} />
        <Route path="/reminders" element={<ProtectedPage><RemindersPage /></ProtectedPage>} />
        <Route path="/manual-vouchers" element={<ProtectedPage><ManualVouchersPage /></ProtectedPage>} />
        <Route path="/financial" element={<ProtectedPage><FinancialDashboardPage /></ProtectedPage>} />
        <Route path="/compliance" element={<ProtectedPage><ComplianceCalendarPage /></ProtectedPage>} />
        <Route path="/email-inbox" element={<ProtectedPage><EmailInboxPage /></ProtectedPage>} />
        <Route path="/anomaly" element={<ProtectedPage><AnomalyDetectionPage /></ProtectedPage>} />
        <Route path="/vyapar" element={<ProtectedPage><VyaparIntegrationPage /></ProtectedPage>} />
        <Route path="/ai-assistant" element={<ProtectedPage><AIAssistantPage /></ProtectedPage>} />
        <Route path="/developer" element={<ProtectedPage><DeveloperPage /></ProtectedPage>} />
        <Route path="/whitelabel" element={<ProtectedPage><WhiteLabelPage /></ProtectedPage>} />
        <Route path="/predictive" element={<ProtectedPage><PredictiveAnalyticsPage /></ProtectedPage>} />
        <Route path="/multi-erp" element={<ProtectedPage><MultiERPPage /></ProtectedPage>} />
        <Route path="/ar-ap" element={<ProtectedPage><ARAPDashboardPage /></ProtectedPage>} />
        <Route path="/quotations" element={<ProtectedPage><QuotationsPage /></ProtectedPage>} />
        <Route path="/tally-queue" element={<ProtectedPage><TallyQueuePage /></ProtectedPage>} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/client-upload/:token" element={<ClientUploadPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}
